import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { colors } from '../theme/theme';

interface BrandLike {
  name: string;
  appSource?: string;
  supportEmail?: string;
  status: 'active' | 'suspended';
  hasOwnGateway?: boolean;
}

interface Props {
  brand: BrandLike;
  /** The app-level half of whitelabelConfig, read off any one location. */
  whitelabel: Record<string, any>;
  locationCount: number;
  /** Brand document fields (name, appSource, supportEmail, status). */
  onSaveBrand: (data: Record<string, any>) => Promise<void>;
  /** App fields, written to every location in one call. */
  onSaveWhitelabel: (fields: Record<string, any>) => Promise<void>;
  onError: (message: string) => void;
}

type Field = { key: string; label: string; help?: string; placeholder?: string; type?: 'url' | 'color' };

/**
 * The long app list, split into the questions an operator actually asks:
 * "what is it called", "where do links go", "where is the app", "what colour".
 */
const APP_GROUPS: { title: string; fields: Field[] }[] = [
  {
    title: 'Identity',
    fields: [
      { key: 'appName', label: 'App / brand name', help: "Shown in emails as 'booked through …'" },
      { key: 'senderName', label: 'Email sender name', help: 'The From name in the inbox' },
      { key: 'logoUrl', label: 'Logo URL', help: 'Used in emails, not in the app itself', placeholder: 'https://', type: 'url' },
    ],
  },
  {
    title: 'Web links',
    fields: [
      { key: 'websiteUrl', label: 'Website URL', placeholder: 'https://', type: 'url' },
      { key: 'privacyPolicyUrl', label: 'Privacy policy URL', placeholder: 'https://', type: 'url' },
      { key: 'termsUrl', label: 'Terms URL', placeholder: 'https://', type: 'url' },
    ],
  },
  {
    title: 'App stores & deep links',
    fields: [
      { key: 'playStoreUrl', label: 'Play Store URL', placeholder: 'https://play.google.com/…', type: 'url' },
      { key: 'appStoreUrl', label: 'App Store URL', placeholder: 'https://apps.apple.com/…', type: 'url' },
      { key: 'deepLinkScheme', label: 'Deep link scheme', placeholder: 'e.g. purplepilates' },
      { key: 'linkBaseUrl', label: 'Share link base URL', help: 'Base of links shared from the app', placeholder: 'https://', type: 'url' },
    ],
  },
  {
    title: 'Colours',
    fields: [
      { key: 'primaryColor', label: 'Primary colour', placeholder: '#6B4EFF', type: 'color' },
      { key: 'accentColor', label: 'Accent colour', placeholder: '#FFB800', type: 'color' },
    ],
  },
];

const APP_KEYS = APP_GROUPS.flatMap((g) => g.fields.map((f) => f.key));
const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldSx = {
  '& .MuiOutlinedInput-root': { bgcolor: colors.white },
};

/** Grey rounded panel holding rows split by hairlines — the layout of the reference design. */
const Panel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      bgcolor: colors.backgroundTertiary,
      border: `1px solid ${colors.divider}`,
      borderRadius: 3,
      px: { xs: 2, sm: 3 },
      '& > *:not(:last-child)': { borderBottom: `1px solid ${colors.divider}` },
    }}
  >
    {children}
  </Box>
);

/** Label on the left on desktop, stacked above the input on a phone. */
/**
 * `inline` is for a small control (switch, chip): the text takes the full
 * width and the control sits at the right edge, on every screen size.
 */
const Row: React.FC<{ label: string; help?: string; inline?: boolean; children: React.ReactNode }> = ({
  label, help, inline, children,
}) => (
  <Stack
    direction={inline ? 'row' : { xs: 'column', md: 'row' }}
    spacing={inline ? 2 : { xs: 1, md: 3 }}
    alignItems={inline ? 'center' : { md: 'center' }}
    sx={{ py: 2 }}
  >
    <Box sx={inline ? { flex: 1, minWidth: 0 } : { width: { md: 240 }, flexShrink: 0 }}>
      <Typography variant="body2" fontWeight={600}>{label}</Typography>
      {help && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
          {help}
        </Typography>
      )}
    </Box>
    <Box sx={inline ? { flexShrink: 0 } : { flex: 1, minWidth: 0 }}>{children}</Box>
  </Stack>
);

const SectionTitle: React.FC<{ title: string; subtitle?: React.ReactNode }> = ({ title, subtitle }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="h6" fontWeight={700}>{title}</Typography>
    {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
  </Box>
);

const BrandSettingsTab: React.FC<Props> = ({
  brand, whitelabel, locationCount, onSaveBrand, onSaveWhitelabel, onError,
}) => {
  const initial = useMemo(() => ({
    name: brand.name ?? '',
    appSource: brand.appSource ?? '',
    supportEmail: brand.supportEmail ?? '',
    active: brand.status === 'active',
    app: Object.fromEntries(APP_KEYS.map((k) => [k, whitelabel?.[k] ?? ''])) as Record<string, string>,
  }), [brand, whitelabel]);

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  // Fresh data from the server (after a save, or another tab) replaces the draft.
  useEffect(() => { setForm(initial); }, [initial]);

  const setApp = (key: string, value: string) => setForm((f) => ({ ...f, app: { ...f.app, [key]: value } }));

  const brandChanges = useMemo(() => {
    const out: Record<string, any> = {};
    if (form.name.trim() !== initial.name) out.name = form.name.trim();
    if (form.appSource.trim() !== initial.appSource) out.appSource = form.appSource.trim();
    // Cleared → null (accepted), not '' (fails email validation).
    if (form.supportEmail.trim() !== initial.supportEmail) out.supportEmail = form.supportEmail.trim() || null;
    if (form.active !== initial.active) out.status = form.active ? 'active' : 'suspended';
    return out;
  }, [form, initial]);

  const appChanges = useMemo(() => {
    const out: Record<string, string> = {};
    APP_KEYS.forEach((k) => { if ((form.app[k] ?? '') !== (initial.app[k] ?? '')) out[k] = form.app[k]; });
    return out;
  }, [form, initial]);

  const changeCount = Object.keys(brandChanges).length + Object.keys(appChanges).length;
  const dirty = changeCount > 0;

  const errors: Record<string, string> = {};
  if (!form.name.trim()) errors.name = 'Brand name is required';
  // The app source is the app's identity: pack isolation, booking sources,
  // push routing and settlement for EVERY location hang off it.
  if (!form.appSource.trim()) errors.appSource = 'Every location answers to this — it cannot be empty';
  if (form.supportEmail.trim() && !EMAIL.test(form.supportEmail.trim())) errors.supportEmail = 'Not a valid email';
  ['primaryColor', 'accentColor'].forEach((k) => {
    if (form.app[k] && !HEX.test(form.app[k])) errors[k] = 'Use a hex value like #6B4EFF';
  });
  const invalid = Object.keys(errors).length > 0;

  const save = async () => {
    if (!dirty || invalid) return;
    if ('appSource' in brandChanges && !window.confirm(
      `Change the app source from "${initial.appSource || '(none)'}" to "${brandChanges.appSource}" for all ${locationCount} location(s)? Apps and settlement use this value.`,
    )) return;
    if (brandChanges.status === 'suspended' && !window.confirm('Suspend this brand?')) return;

    setSaving(true);
    try {
      if (Object.keys(brandChanges).length) await onSaveBrand(brandChanges);
      if (Object.keys(appChanges).length) await onSaveWhitelabel(appChanges);
    } catch (e: any) {
      onError(e?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f: Field) => {
    const value = form.app[f.key] ?? '';
    const isColor = f.type === 'color';
    return (
      <Row key={f.key} label={f.label} help={f.help}>
        <TextField
          value={value}
          onChange={(e) => setApp(f.key, e.target.value)}
          placeholder={f.placeholder}
          fullWidth
          size="small"
          sx={fieldSx}
          error={!!errors[f.key]}
          helperText={errors[f.key]}
          InputProps={isColor ? {
            startAdornment: (
              <InputAdornment position="start">
                <Box
                  component="input"
                  type="color"
                  value={HEX.test(value) && value.length === 7 ? value : '#ffffff'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApp(f.key, e.target.value.toUpperCase())}
                  sx={{
                    width: 26, height: 26, p: 0, border: `1px solid ${colors.divider}`,
                    borderRadius: 1, cursor: 'pointer', bgcolor: 'transparent',
                    '&::-webkit-color-swatch-wrapper': { p: 0 },
                    '&::-webkit-color-swatch': { border: 'none', borderRadius: 4 },
                  }}
                />
              </InputAdornment>
            ),
          } : undefined}
        />
      </Row>
    );
  };

  return (
    <Box sx={{ maxWidth: 880, pb: 2 }}>
      {/* ── 1. Brand details ─────────────────────────────────────────── */}
      <SectionTitle title="Brand details" />
      <Panel>
        <Row label="Brand name">
          <TextField
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth size="small" sx={fieldSx} error={!!errors.name} helperText={errors.name}
          />
        </Row>
        <Row label="App source" help="One brand, one source. Every location answers to it.">
          <TextField
            value={form.appSource} onChange={(e) => setForm({ ...form, appSource: e.target.value })}
            fullWidth size="small" sx={fieldSx} error={!!errors.appSource} helperText={errors.appSource}
          />
        </Row>
        <Row label="Support email" help="Where a location with no login of its own gets emailed.">
          <TextField
            value={form.supportEmail} onChange={(e) => setForm({ ...form, supportEmail: e.target.value })}
            placeholder="support@brand.com" type="email"
            fullWidth size="small" sx={fieldSx} error={!!errors.supportEmail} helperText={errors.supportEmail}
          />
        </Row>
      </Panel>

      {/* ── 2. App & branding ────────────────────────────────────────────
          These describe ONE app binary, so they are edited here once and
          written to every location. The data still lives on each studio's
          whitelabelConfig — nothing moved. */}
      <Box sx={{ mt: 4 }}>
        <SectionTitle
          title="App & branding"
          subtitle={<>Shared by every location. Saving writes to all {locationCount} of them, so no location can drift. Razorpay and support contacts stay per location.</>}
        />
        <Stack spacing={2.5}>
          {APP_GROUPS.map((g) => (
            <Box key={g.title}>
              <Typography
                variant="caption"
                sx={{ display: 'block', mb: 1, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: colors.primaryDark }}
              >
                {g.title}
              </Typography>
              <Panel>{g.fields.map(renderField)}</Panel>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* ── 3. Status & payments ─────────────────────────────────────── */}
      <Box sx={{ mt: 4 }}>
        <SectionTitle title="Status & payments" />
        <Panel>
          <Row inline label="Brand is active" help="Suspending hides the brand without deleting anything.">
            <Stack direction="row-reverse" alignItems="center" spacing={1}>
              <Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              <Typography variant="body2" color={form.active ? 'success.main' : 'warning.main'} fontWeight={600}>
                {form.active ? 'Active' : 'Suspended'}
              </Typography>
            </Stack>
          </Row>
          <Row
            inline
            label="Payments"
            help="Each location collects into its own Razorpay account when it has one, otherwise SweatSocial’s. A brand-level account is not used yet."
          >
            <Chip
              size="small"
              color={brand.hasOwnGateway ? 'success' : 'default'}
              label={brand.hasOwnGateway ? 'Own Razorpay account' : 'Uses SweatSocial'}
            />
          </Row>
        </Panel>
      </Box>

      {/* ── Save bar: sticks to the bottom of the screen while scrolling ── */}
      <Box
        sx={{
          position: 'sticky', bottom: 0, zIndex: 5, mt: 4, mx: { xs: -2, md: 0 },
          px: { xs: 2, md: 0 }, py: 2,
          bgcolor: colors.white,
          borderTop: `1px solid ${dirty ? colors.primaryLight : colors.divider}`,
        }}
      >
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
            {dirty ? `${changeCount} unsaved change${changeCount === 1 ? '' : 's'}` : 'All changes saved'}
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ '& > *': { flex: { xs: 1, sm: 'none' } } }}>
            <Button
              onClick={() => setForm(initial)}
              disabled={!dirty || saving}
              sx={{
                borderRadius: 999, px: 4, color: colors.primaryDark,
                bgcolor: `${colors.primary}26`, '&:hover': { bgcolor: `${colors.primary}40` },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={save}
              disabled={!dirty || invalid || saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ borderRadius: 999, px: 4 }}
            >
              {saving ? 'Saving…' : 'Update settings'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default BrandSettingsTab;
